import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Table } from 'antd';
import dayjs from 'dayjs';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import milestoneInitialColumns from '@/utils/column/milestoneInitialColumn';
import { getMilestoneBinValue } from '@/utils/helpers/getMilestoneBinValue';
import AddMilestoneModal from '../MilestoneModal';
import getSimplifiedPlanningSummary from './getMilestonePlanningSummary';
import getMilestonePlanningSummary from '@/utils/filters/getPlanningSummary';

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: '10px',
            height: '100%',
            cursor: 'col-resize',
            background: '#1890ff',
            opacity: 0,
            transition: 'opacity 0.3s',
            borderRadius: '2px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0';
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const Milestones = ({
  milestones,
  otherElementRef,
  users,
  colorByAttribute,
  milestoneColors,
  initiativeColors,
  initiatives,
  visibleColumn,
  setMilestoneCurrentFilter,
  setCurrentFilter,
  setFilterVisible,
  milestoneCurrentFilter,
  setFilterHistory,
}) => {
  const initiativeColumns = [
    'initiativeName',
    'initiativeNumber',
    'initiativeOwnerInfo',
    'workstreamSponsorWorkstream',
    'workstreamLeadSubworkstream'
  ];

  const [isAddMilestoneModalOpen, setIsAddMilestoneModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [dynamicPadding, setDynamicPadding] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});

  // State for column widths
  const [columnWidths, setColumnWidths] = useState({});

  // Function to handle row click
  const handleRowClick = (record) => {
    const parentInitiative = initiatives.find((initiativeItem) =>
      initiativeItem.milestones?.find(
        (milestoneItem) => milestoneItem._id === record._id
      )
    );

    if (parentInitiative) {
      setSelectedMilestone(record);
      setSelectedInitiative(parentInitiative);
      setIsAddMilestoneModalOpen(true);
    } else {
      console.warn("Could not find parent initiative for milestone:", record);
    }
  };

  const renderWithColor = useCallback((value, color) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {color && (
        <span
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: color,
            marginRight: '8px',
            display: 'inline-block',
          }}
        />
      )}
      {value}
    </div>
  ), []);

  const milestoneToInitiative = new Map();
  initiatives.forEach(initiative => {
    initiative.milestones?.forEach(m => {
      milestoneToInitiative.set(m._id, initiative);
    });
  });

  const getInitiative = (milestone) => milestoneToInitiative.get(milestone._id);

  // Handle column resize
  const handleResize = useCallback((key) => (e, { size }) => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: size.width
    }));
  }, []);

  const columns = useMemo(() => {
    const getStartDate = (milestone) => milestone.startDate ? dayjs(milestone.startDate).toDate() : null;
    const getEndDate = (milestone) => milestone.endDate ? dayjs(milestone.endDate).toDate() : null;

    const findParentInitiative = (milestoneRecord) =>
      initiatives.find((i) => i.milestones?.some((m) => m._id === milestoneRecord._id));

    // Helper to get the latest user from the parent initiative's history for a specific milestone action
    const getLatestUserFromMilestoneHistory = (milestoneRecord, actionSubstring) => {
      const parent = findParentInitiative(milestoneRecord);
      if (!parent || !parent.history) return 'N/A';

      const matches = parent.history.filter(entry =>
        entry.action?.includes(actionSubstring) &&
        (entry.action?.includes(`Milestone with Number ${milestoneRecord.milestoneNumber}`) ||
          entry.action?.includes(`Milestone #${milestoneRecord.name}`))
      ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const userFound = users?.find(u => u._id === matches[0]?.user);
      return userFound ? `${userFound.firstName} ${userFound.lastName}` : 'N/A';
    };

    // Helper to get the workstream name from the parent initiative
    const getWorkstreamName = (milestoneRecord) => {
      const parentInitiative = findParentInitiative(milestoneRecord);
      const workstreams = parentInitiative?.workstreamSponsorWorkstream;
      return Array.isArray(workstreams) ? workstreams.map(ws => ws?.workstreamName).filter(Boolean).join(', ') : 'Not Assigned';
    };

    // Helper to get the subworkstream name from the parent initiative
    const getSubworkstreamName = (milestoneRecord) => {
      const parentInitiative = findParentInitiative(milestoneRecord);
      const subworkstreams = parentInitiative?.workstreamLeadSubworkstream;
      return Array.isArray(subworkstreams) ? subworkstreams.map(sws => sws?.subworkstreamName).filter(Boolean).join(',') : 'Not Assigned';
    };

    // Helper to get the initiative owner name from the parent initiative
    const getOwnerName = (milestoneRecord) => {
      const parentInitiative = findParentInitiative(milestoneRecord);
      const ownerNames = parentInitiative?.initiativeOwnerInfo;
      return Array.isArray(ownerNames) ? ownerNames.map(owner => `${owner.firstName} ${owner.lastName}`).filter(Boolean).join(',') : 'Not Assigned';
    };

    return milestoneInitialColumns.map((col) => {
      const newCol = { ...col };

      // Set initial width from columnWidths state or default
      newCol.width = columnWidths[col.key] || col.width || 150;

      newCol.filterDropdown = () => null;
      newCol.onFilterDropdownVisibleChange = (visible) => {
        if (visible) {
          setFilterVisible(true);
          if (initiativeColumns.includes(col.key)) {
            setCurrentFilter(col.key);
          } else {
            setMilestoneCurrentFilter(col.key);
          }
          setFilterHistory((prevHistory) => [...prevHistory, col.key, 'Filter Options']);
        }
      };

      switch (col.key) {
        case 'milestoneNumber':
          newCol.sorter = (a, b) => parseInt(a.milestoneNumber) - parseInt(b.milestoneNumber);
          newCol.render = (_, record) => {
            const nameNumber = record.milestoneNumber;
            const color = colorByAttribute === 'milestoneNumber' ? milestoneColors[`${record.milestoneNumber} - ${record.name}`] : null;
            return renderWithColor(nameNumber, color);
          };
          break;
        case 'name':
        case 'type':
          newCol.sorter = (a, b) => (a[col.key] || '').localeCompare(b[col.key] || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === col.key ? milestoneColors[record[col.key]] : null;
            return renderWithColor(text, color);
          };
          break;
        case 'Milestone Status':
          newCol.sorter = (a, b) => (a.status).localeCompare(b.status);
          newCol.render = (_, record) => {
            const status = record.status;
            const color = colorByAttribute === 'status' ? milestoneColors[record.status] : null;
            return renderWithColor(status, color);
          }
          break;
        case 'milestoneNumber - name':
          newCol.sorter = (a, b) => parseInt(a.milestoneNumber) - parseInt(b.milestoneNumber);
          newCol.render = (_, record) => {
            const nameNumber = `${record.milestoneNumber} - ${record.name}`;
            const color = colorByAttribute === 'milestoneNumber - name' ? milestoneColors[`${record.milestoneNumber} - ${record.name}`] : null;
            return renderWithColor(nameNumber, color);
          };
          break;
        case 'Owner':
          newCol.sorter = (a, b) => {
            const ownerA = users?.find((user) => user._id === a.Owner);
            const ownerB = users?.find((user) => user._id === b.Owner);
            const nameA = ownerA ? `${ownerA.firstName} ${ownerA.lastName}` : '';
            const nameB = ownerB ? `${ownerB.firstName} ${ownerB.lastName}` : '';
            return nameA.localeCompare(nameB);
          };
          newCol.render = (ownerId) => {
            if (!ownerId || !users) return 'Not Assigned';
            const owner = users.find((user) => user._id === ownerId);
            const ownerName = owner ? `${owner.firstName || ''} ${owner.lastName || ''}` : 'Not Found';
            const color = colorByAttribute === 'Owner' ? milestoneColors[ownerName] : null;
            return renderWithColor(ownerName, color);
          };
          break;

        case 'Work starting date':
          newCol.sorter = (a, b) => (getStartDate(a)?.getTime() || 0) - (getStartDate(b)?.getTime() || 0);
          newCol.render = (_, record) => {
            const plannedDate = getStartDate(record);
            const displayDate = plannedDate ? plannedDate.toLocaleDateString() : 'No Date';
            const color = colorByAttribute === 'Work starting date' ? milestoneColors[displayDate] : null;
            return renderWithColor(displayDate, color);
          };
          break;

        case 'Milestone completion date':
          newCol.sorter = (a, b) => (getEndDate(a)?.getTime() || 0) - (getEndDate(b)?.getTime() || 0);
          newCol.render = (_, record) => {
            const plannedDate = getEndDate(record);
            const displayDate = plannedDate ? plannedDate.toLocaleDateString() : 'No Date';
            const color = colorByAttribute === 'Milestone completion date' ? milestoneColors[displayDate] : null;
            return renderWithColor(displayDate, color);
          };
          break;
        case 'Milestone Planning summary':
          newCol.sorter = (a, b) => getMilestonePlanningSummary(a, findParentInitiative(a)).localeCompare(getMilestonePlanningSummary(b, findParentInitiative(b)));
          newCol.render = (_, milestone) => {
            const planningSummary = getMilestonePlanningSummary(milestone, findParentInitiative(milestone));
            const color = colorByAttribute === 'planningSummary' ? milestoneColors[planningSummary] : null;
            return renderWithColor(planningSummary, color);
          };
          break;
        case 'Planning summary (simplified)':
          newCol.sorter = (a, b) =>
            getSimplifiedPlanningSummary(a, findParentInitiative(a)).localeCompare(
              getSimplifiedPlanningSummary(b, findParentInitiative(b))
            );
          newCol.render = (_, milestone) => {
            const simplifiedSummary = getSimplifiedPlanningSummary(milestone, findParentInitiative(milestone));
            const color =
              colorByAttribute === 'Planning summary (simplified)'
                ? milestoneColors[simplifiedSummary]
                : null;
            return renderWithColor(simplifiedSummary, color);
          };
          break;
        case 'Updated On':
          newCol.sorter = (a, b) => {
            const getLatestTimestamp = (milestoneNumber) => {
              const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
              const matches = historyEntries.filter((entry) =>
                entry.action?.includes(`Milestone with Number ${milestoneNumber} has been updated.`)
              );
              matches.sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp));
              return matches[0]?.timestamp || 0;
            };
            return new Date(getLatestTimestamp(a.milestoneNumber)) - new Date(getLatestTimestamp(b.milestoneNumber));
          };
          newCol.render = (text, record) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const matchingEntries = historyEntries
              .filter((entry) => entry.action?.includes(`Milestone with Number ${record.milestoneNumber} has been updated.`))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const latestEntry = matchingEntries[0];
            const updatedOnDate = latestEntry?.timestamp ? new Date(latestEntry.timestamp).toLocaleDateString() : 'N/A';
            const color = colorByAttribute === 'Updated on' ? milestoneColors[updatedOnDate] : null;
            return renderWithColor(updatedOnDate, color);
          };
          break;

        case 'Created On':
          newCol.sorter = (a, b) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const getLatestTimestamp = (milestoneName) => {
              const matches = historyEntries
                .filter((entry) => entry.action?.includes(`Milestone #${milestoneName} created`))
                .sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp));
              return matches[0]?.timestamp || 0;
            };
            return new Date(getLatestTimestamp(a.name)) - new Date(getLatestTimestamp(b.name));
          };
          newCol.render = (text, record) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const matchingEntries = historyEntries
              .filter((entry) => entry.action?.includes(`Milestone #${record.name} created`))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const latestCreated = matchingEntries[0];
            const createdDate = latestCreated?.timestamp ? new Date(latestCreated.timestamp).toLocaleDateString() : 'N/A';
            const color = colorByAttribute === 'Created On' ? milestoneColors[createdDate] : null;
            return renderWithColor(createdDate, color);
          };
          break;

        case 'Created by':
          newCol.sorter = (a, b) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const getLatestCreatorName = (milestoneName) => {
              const matches = historyEntries
                .filter((entry) => entry.action?.includes(`Milestone #${milestoneName} created`))
                .sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp));
              const latest = matches[0];
              const creator = users?.find((u) => u._id === latest?.user);
              return creator ? `${creator.firstName} ${creator.lastName}` : '';
            };
            return getLatestCreatorName(a.name).localeCompare(getLatestCreatorName(b.name));
          };
          newCol.render = (text, record) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const matchingEntries = historyEntries
              .filter((entry) => entry.action?.includes(`Milestone #${record.name} created`))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const latestCreated = matchingEntries[0];
            const creator = users?.find((u) => u._id === latestCreated?.user);
            const creatorName = creator ? `${creator.firstName} ${creator.lastName}` : 'N/A';
            const color = colorByAttribute === 'Created by' ? milestoneColors[creatorName] : null;
            return renderWithColor(creatorName, color);
          };
          break;

        case 'Updated by':
          newCol.sorter = (a, b) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const getLatestUpdaterName = (milestoneNumber) => {
              const matches = historyEntries
                .filter((entry) => entry.action?.includes(`Milestone with Number ${milestoneNumber} has been updated.`))
                .sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp));
              const updater = users?.find((u) => u._id === matches[0]?.user);
              return updater ? `${updater.firstName} ${updater.lastName}` : '';
            };
            return getLatestUpdaterName(a.milestoneNumber).localeCompare(getLatestUpdaterName(b.milestoneNumber));
          };
          newCol.render = (text, record) => {
            const historyEntries = initiatives.flatMap((initiative) => initiative.history || []);
            const matchingEntries = historyEntries
              .filter((entry) => entry.action?.includes(`Milestone with Number ${record.milestoneNumber} has been updated.`))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const latestEntry = matchingEntries[0];
            const updater = users?.find((u) => u._id === latestEntry?.user);
            const updaterName = updater ? `${updater.firstName} ${updater.lastName}` : 'N/A';
            const color = colorByAttribute === 'Updated by' ? milestoneColors[updaterName] : null;
            return renderWithColor(updaterName, color);
          };
          break;

        case 'planningCheck':
          newCol.sorter = (a, b) => a.planningCheck.localeCompare(b.planningCheck);
          newCol.render = (planningCheck, record) => {
            const color = colorByAttribute === 'planningCheck' ? milestoneColors[record.planningCheck] : null;
            return renderWithColor(planningCheck, color);
          };
          break;
        case 'Change in Milestone completion date (binned, current vs last week)':
          newCol.sorter = (a, b) => {
            const parentA = findParentInitiative(a);
            const parentB = findParentInitiative(b);
            const binValueA = getMilestoneBinValue(a, parentA);
            const binValueB = getMilestoneBinValue(b, parentB);
            const order = [
              '<not set>',
              'Accelerated',
              'Unchanged',
              'Delayed (1 to 7 days)',
              'Delayed (8 to 14 days)',
              'Delayed (15 to 30 days)',
              'Delayed (more than 30 days)',
            ];
            return order.indexOf(binValueA) - order.indexOf(binValueB);
          };
          newCol.render = (text, record) => {
            const parent = findParentInitiative(record);
            const binValue = getMilestoneBinValue(record, parent);
            const color = colorByAttribute === 'Change in Milestone completion date (binned, current vs last week)' ? milestoneColors[binValue] : null;
            return renderWithColor(binValue, color);
          };
          newCol.onFilter = (value, record) => {
            const parent = findParentInitiative(record);
            return getMilestoneBinValue(record, parent) === value;
          };
          break;
        case 'initiativeNumber':
          newCol.sorter = (a, b) => {
            const aNumber = findParentInitiative(a)?.initiativeNumber || '';
            const bNumber = findParentInitiative(b)?.initiativeNumber || '';
            return aNumber.localeCompare(bNumber);
          };
          newCol.render = (_, milestoneRecord) => {
            const parentInitiativeRecord = findParentInitiative(milestoneRecord);
            const initiativeNumber = parentInitiativeRecord?.initiativeNumber || 'N/A';
            const color = colorByAttribute === 'initiativeNumber' ? initiativeColors[initiativeNumber] : null;
            return renderWithColor(initiativeNumber, color);
          };
          break;
        case 'initiativeName':
          newCol.sorter = (a, b) => {
            const aName = findParentInitiative(a)?.initiativeName || '';
            const bName = findParentInitiative(b)?.initiativeName || '';
            return aName.localeCompare(bName);
          };
          newCol.render = (_, milestoneRecord) => {
            const parentInitiativeRecord = findParentInitiative(milestoneRecord);
            const initiativeName = parentInitiativeRecord?.initiativeName || 'N/A';
            const color = colorByAttribute === 'initiativeName' ? initiativeColors[initiativeName] : null;
            return renderWithColor(initiativeName, color);
          };
          break;

        case 'workstreamSponsorWorkstream':
          newCol.sorter = (a, b) => getWorkstreamName(a).localeCompare(getWorkstreamName(b));
          newCol.render = (_, milestoneRecord) => {
            const workstreamNames = getWorkstreamName(milestoneRecord);
            const color = colorByAttribute === 'workstreamSponsorWorkstream' ? initiativeColors[workstreamNames] : null;
            return renderWithColor(workstreamNames, color);
          };
          break;
        case 'workstreamLeadSubworkstream':
          newCol.sorter = (a, b) => getSubworkstreamName(a).localeCompare(getSubworkstreamName(b));
          newCol.render = (_, milestoneRecord) => {
            const subworkstreams = getSubworkstreamName(milestoneRecord);
            const color = colorByAttribute === 'workstreamLeadSubworkstream' ? initiativeColors[subworkstreams] : null;
            return renderWithColor(subworkstreams, color);
          };
          break;
        case 'initiativeOwnerInfo':
          newCol.sorter = (a, b) => getOwnerName(a).localeCompare(getOwnerName(b));
          newCol.render = (_, milestoneRecord) => {
            const ownerName = getOwnerName(milestoneRecord);
            const color = colorByAttribute === 'initiativeOwnerInfo' ? initiativeColors[ownerName] : null;
            return renderWithColor(ownerName, color);
          };
          break;
      }

      // Add resize functionality to column
      newCol.onHeaderCell = (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      });

      return newCol;
    });
  }, [milestones, users, colorByAttribute, milestoneColors, initiativeColors, initiatives, setFilterVisible, setMilestoneCurrentFilter, setFilterHistory, renderWithColor, columnWidths, handleResize]);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  useEffect(() => {
    const calculatePadding = () => {
      if (otherElementRef && otherElementRef.current) {
        setDynamicPadding(otherElementRef.current.offsetWidth + 20);
      } else {
        setDynamicPadding(20);
      }
    };

    calculatePadding();
    window.addEventListener('resize', calculatePadding);
    return () => window.removeEventListener('resize', calculatePadding);
  }, [otherElementRef]);

  // Components for resize functionality
  const components = {
    header: {
      cell: ResizableTitle,
    },
  };

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table
        components={components}
        columns={columns.filter(
          (col) => visibleColumn.includes(col.key) || col.key === colorByAttribute
        )}
        dataSource={milestones}
        scroll={{ x: 'max-content' }}
        style={{ width: '100%' }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
        rowKey='_id'
      />
      {selectedMilestone && selectedInitiative && (
        <AddMilestoneModal
          isAddMilestoneModalOpen={isAddMilestoneModalOpen}
          setAddMilestoneModal={setIsAddMilestoneModalOpen}
          milestoneDetails={selectedMilestone}
          setInitiative={setSelectedInitiative}
          edit={true}
          initiative={selectedInitiative}
          onMilestoneCreate={() => {
            setIsAddMilestoneModalOpen(false);
            setSelectedMilestone(null);
            setSelectedInitiative(null);
          }}
          users={users}
        />
      )}
    </div>
  );
};

export default Milestones;